import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  TableCell,
  TableCellProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { TONE_STYLE } from "../wip/buyMedia/columns";
import {
  CHANNEL_COLORS,
  CM_COLOR,
  MONTHS,
  MONTHS_SHORT,
  PM_COLOR,
  Tone,
  keur,
} from "./controlBoardModel";
import { ControlBoardChannel } from "../../types/controlBoard";

/** Texto con el color del semáforo. El texto ya dice el valor; el color no es el único canal. */
export const Signal: React.FC<{ text: string; tone: Tone }> = ({
  text,
  tone,
}) => {
  const theme = useTheme();
  if (tone === "none")
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        {text}
      </Typography>
    );
  const style = TONE_STYLE[theme.palette.mode][tone];
  return (
    <Chip
      size="small"
      label={text}
      sx={{
        height: 22,
        fontWeight: 600,
        fontSize: 12,
        color: style.color,
        backgroundColor: style.bg,
      }}
    />
  );
};

/** Color de texto de un tono, para cifras sueltas. */
export const useToneColor = () => {
  const theme = useTheme();
  return (tone: Tone): string | undefined =>
    tone === "none" ? undefined : TONE_STYLE[theme.palette.mode][tone].color;
};

export const ChannelSwatch: React.FC<{
  channel?: ControlBoardChannel;
  color?: string;
}> = ({ channel, color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "3px",
      mr: 1,
      verticalAlign: "-1px",
      flex: "none",
      backgroundColor: color ?? (channel ? CHANNEL_COLORS[channel] : "#999"),
      boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
    }}
  />
);

/** Flecha de una fila desplegable. */
export const Caret: React.FC<{
  open: boolean;
  onClick?: () => void;
  label?: string;
}> = ({ open, onClick, label }) => (
  <IconButton
    size="small"
    onClick={(event) => {
      event.stopPropagation();
      onClick?.();
    }}
    aria-label={label ?? (open ? "Plegar" : "Desplegar")}
    aria-expanded={open}
    sx={{ p: 0.25, mr: 0.5 }}
  >
    <KeyboardArrowRightIcon
      fontSize="small"
      sx={{
        transition: "transform .15s",
        transform: open ? "rotate(90deg)" : "none",
      }}
    />
  </IconButton>
);

/** Celda numérica alineada a la derecha. */
export const Num: React.FC<TableCellProps & { muted?: boolean }> = ({
  muted,
  sx,
  children,
  ...rest
}) => (
  <TableCell
    align="right"
    sx={{
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
      color: muted ? "text.secondary" : undefined,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </TableCell>
);

/** Año entero o un mes concreto, hasta el último mes con datos. */
export const PeriodSelector: React.FC<{
  value: number;
  lastMonth: number;
  onChange: (month: number) => void;
}> = ({ value, lastMonth, onChange }) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={value}
    onChange={(_, next: number | null) => next !== null && onChange(next)}
    aria-label="Periodo"
    sx={{ flexWrap: "wrap" }}
  >
    <ToggleButton value={0}>Año</ToggleButton>
    {Array.from({ length: lastMonth }, (_, i) => i + 1).map((m) => (
      <ToggleButton key={m} value={m} aria-label={MONTHS[m]}>
        {MONTHS_SHORT[m]}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

export interface DonutPart {
  label: string;
  value: number;
  color: string;
}

/** Anillo con el total en el centro. */
export const Donut: React.FC<{
  parts: DonutPart[];
  centerValue: string;
  centerLabel: string;
  size?: number;
}> = ({ parts, centerValue, centerLabel, size = 180 }) => {
  const theme = useTheme();
  const data = parts.filter((p) => p.value > 0);
  const total = data.reduce((t, p) => t + p.value, 0);
  return (
    <Box sx={{ position: "relative", width: size, height: size, flex: "none" }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={
              data.length
                ? data
                : [{ label: "", value: 1, color: theme.palette.grey[200] }]
            }
            dataKey="value"
            nameKey="label"
            innerRadius="64%"
            outerRadius="96%"
            stroke={theme.palette.background.paper}
            strokeWidth={2}
            isAnimationActive={false}
          >
            {(data.length ? data : [{ color: theme.palette.grey[200] }]).map(
              (p, i) => (
                <Cell key={i} fill={p.color} />
              )
            )}
          </Pie>
          {data.length > 0 && (
            <Tooltip
              formatter={(value: number, name: string) => [
                `${keur(value)} · ${Math.round((value / total) * 100)} %`,
                name,
              ]}
              contentStyle={{
                background: theme.palette.background.paper,
                borderRadius: 8,
                border: `1px solid ${theme.palette.grey[200]}`,
                fontSize: 12,
              }}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.2 }}
        >
          {centerValue}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {centerLabel}
        </Typography>
      </Box>
    </Box>
  );
};

/** Leyenda de un reparto: color, nombre y peso. */
export const ShareLegend: React.FC<{ parts: DonutPart[] }> = ({ parts }) => {
  const total = parts.reduce((t, p) => t + Math.max(0, p.value), 0);
  return (
    <Stack gap={0.5} sx={{ minWidth: 180 }}>
      {parts
        .filter((p) => p.value > 0)
        .map((p) => (
          <Stack
            key={p.label}
            direction="row"
            justifyContent="space-between"
            gap={2}
          >
            <Typography variant="body2">
              <ChannelSwatch color={p.color} />
              {p.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {total ? Math.round((p.value / total) * 100) : 0} %
            </Typography>
          </Stack>
        ))}
    </Stack>
  );
};

/** Barra compra de medios / paid media. */
export const MixBar: React.FC<{ shareCM: number; width?: number }> = ({
  shareCM,
  width = 110,
}) => {
  const theme = useTheme();
  if (!Number.isFinite(shareCM))
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      justifyContent="flex-end"
    >
      <Box
        sx={{
          display: "flex",
          width,
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          backgroundColor: theme.palette.grey[200],
        }}
      >
        <Box sx={{ width: `${shareCM * 100}%`, backgroundColor: CM_COLOR }} />
        <Box sx={{ flex: 1, backgroundColor: PM_COLOR }} />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 38, textAlign: "right" }}>
        {Math.round(shareCM * 100)} %
      </Typography>
    </Stack>
  );
};

/** Leyenda de compra de medios / paid media. */
export const TypeLegend: React.FC = () => (
  <Stack direction="row" gap={2} flexWrap="wrap">
    <Typography variant="caption" color="text.secondary">
      <ChannelSwatch color={CM_COLOR} />
      Compra de medios
    </Typography>
    <Typography variant="caption" color="text.secondary">
      <ChannelSwatch color={PM_COLOR} />
      Paid Media
    </Typography>
  </Stack>
);

/** Anillo pequeño con el peso de compra de medios. */
export const ShareRing: React.FC<{ share: number; size?: number }> = ({
  share,
  size = 84,
}) => (
  <Donut
    size={size}
    parts={[
      { label: "Compra de medios", value: Math.max(0, share), color: CM_COLOR },
      { label: "Paid Media", value: Math.max(0, 1 - share), color: PM_COLOR },
    ]}
    centerValue={Number.isFinite(share) ? `${Math.round(share * 100)} %` : "—"}
    centerLabel=""
  />
);

/** Barra de cumplimiento con la marca del 100 %. */
export const ComplianceBar: React.FC<{
  value: number;
  max?: number;
  color: string;
  width?: number;
}> = ({ value, max = 1.2, color, width = 110 }) => {
  const theme = useTheme();
  const scale = Math.max(max, 1);
  return (
    <Box
      sx={{
        position: "relative",
        width,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.palette.grey[200],
        flex: "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "0 auto 0 0",
          width: `${Math.min(1, Math.max(0, value) / scale) * 100}%`,
          borderRadius: 5,
          backgroundColor: color,
        }}
      />
      <Box
        title="100 %"
        sx={{
          position: "absolute",
          top: -3,
          left: `${(1 / scale) * 100}%`,
          width: 2,
          height: 16,
          backgroundColor: theme.palette.text.primary,
        }}
      />
    </Box>
  );
};

/** Nota pequeña bajo un bloque. */
export const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    component="p"
    sx={{ mt: 1, mb: 0, lineHeight: 1.5 }}
  >
    {children}
  </Typography>
);
